import { motion } from 'framer-motion'
import { useSimStore } from '../store/simStore'
import { useWallClock } from '../hooks/useWallClock'
import { useSimClock } from '../hooks/useSimClock'

interface Props {
  onPause: () => void
  onStop: () => void
}

export function HUD({ onPause, onStop }: Props) {
  const { appState, instanceConfig, kpis, rtFactor } = useSimStore()
  const wall = useWallClock()
  const simTime = useSimClock()
  const horizon = instanceConfig?.horizon ?? 1

  const progress = Math.min(1, simTime / horizon)
  const pct = (progress * 100).toFixed(1)

  return (
    <motion.div
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
      className="absolute top-0 left-0 right-0 z-20 flex items-center gap-4 px-4 py-2 bg-[#0d1117]/90 border-b border-[#30363d] backdrop-blur-sm text-sm font-mono"
    >
      {/* Wall clock */}
      <span className="text-slate-400 whitespace-nowrap">⏱ {wall}</span>

      {/* Sim clock */}
      <span className="text-teal-400 whitespace-nowrap">T = {simTime.toFixed(1)} min</span>

      {/* Progress bar */}
      <div className="flex-1 flex items-center gap-2">
        <div className="flex-1 bg-[#21262d] rounded-full h-2 overflow-hidden">
          <motion.div
            className="h-full bg-teal-500 rounded-full"
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <span className="text-slate-400 text-xs">{pct}%</span>
      </div>

      {/* KPI mini-bar */}
      {kpis && (
        <div className="flex gap-3 text-xs">
          <span className="text-emerald-400">{kpis.n_completed}/{kpis.n_total} ✓</span>
          {kpis.n_late > 0 && <span className="text-rose-400">{kpis.n_late} late</span>}
          <span className="text-teal-400">SPI {kpis.spi.toFixed(3)}</span>
        </div>
      )}

      {/* Speed indicator */}
      <span className="text-slate-500 text-xs whitespace-nowrap" title="+/- to adjust">
        {rtFactor === 0 ? '⚡ full' : `${rtFactor.toFixed(3)}×`}
      </span>

      {/* Live badge */}
      <motion.div
        animate={{ opacity: [1, 0.4, 1] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="flex items-center gap-1 text-xs text-emerald-400"
      >
        <span className="w-2 h-2 bg-emerald-400 rounded-full" />
        Live
      </motion.div>

      {/* Controls */}
      <button
        onClick={onPause}
        className="px-3 py-1 rounded bg-[#21262d] hover:bg-[#30363d] text-slate-300"
        title="Space"
      >
        {appState === 'PAUSED' ? '▶' : '⏸'}
      </button>
      <button
        onClick={onStop}
        className="px-2 py-1 rounded bg-[#21262d] hover:bg-rose-900 text-slate-300"
        title="Stop"
      >
        ✕
      </button>
    </motion.div>
  )
}
